import useSWR, { mutate, type SWRConfiguration } from 'swr';
import useAuth from 'hooks/useAuth';
import useTenantSelection from 'hooks/useTenantSelection';
import axios from 'utils/axios';
import type { ReportRunRequest, ReportDefinition } from './reports';
import type { ReportSharingScope, ReportOutputKind } from './reportSchedules';

// Mirrors Weightrax.Domain.Enums.ExportResponseStatusEnum / ExportJobStatusEnum
export enum ExportJobStatus {
  Pending = 1,
  Running = 2,
  Ready = 3,
  Failed = 4,
  Cancelled = 5,
  Expired = 6
}

// Mirrors Weightrax.Domain.Enums.ExportDeliveryStatusEnum
export enum ExportDeliveryStatus {
  Pending = 1,
  Sending = 2,
  Delivered = 3,
  Failed = 4,
  Skipped = 5
}

// Mirrors Weightrax.Domain.Enums.ExportSourceEnum
export enum ExportSource {
  Manual = 1,
  Scheduled = 2,
  Retry = 3
}

// Mirrors Weightrax.Domain.Enums.ExportErrorCodeEnum
export enum ExportErrorCode {
  TimedOut = 1,
  TooManyRows = 2,
  NoAccess = 3,
  InvalidRequest = 4,
  OutOfRetries = 5,
  MarkedFailed = 6,
  Unexpected = 7
}

export const EXPORT_ERROR_LABELS: Record<ExportErrorCode, string> = {
  [ExportErrorCode.TimedOut]: 'Execution budget exceeded. Narrow date range or filters.',
  [ExportErrorCode.TooManyRows]: 'Row limit exceeded (50,000 lines). Narrow date range or use summary output.',
  [ExportErrorCode.NoAccess]: 'The export owner or schedule scope is no longer accessible.',
  [ExportErrorCode.InvalidRequest]: 'Invalid export configuration or parameters.',
  [ExportErrorCode.OutOfRetries]: 'Automatic attempts exhausted.',
  [ExportErrorCode.MarkedFailed]: 'Manually marked as failed.',
  [ExportErrorCode.Unexpected]: 'An unexpected system error occurred.'
};

export const EXPORT_STATUS_LABELS: Record<ExportJobStatus, string> = {
  [ExportJobStatus.Pending]: 'Pending',
  [ExportJobStatus.Running]: 'Running',
  [ExportJobStatus.Ready]: 'Ready',
  [ExportJobStatus.Failed]: 'Failed',
  [ExportJobStatus.Cancelled]: 'Cancelled',
  [ExportJobStatus.Expired]: 'Expired'
};

export function formatExportStatus(status?: ExportJobStatus | string | null): string {
  if (status === null || status === undefined) return '';
  if (typeof status === 'number') {
    return EXPORT_STATUS_LABELS[status as ExportJobStatus] ?? String(status);
  }
  return status;
}

export function formatExportError(job: { errorCode?: ExportErrorCode | number | null; errorMessage?: string | null }): string | undefined {
  if (job.errorCode && EXPORT_ERROR_LABELS[job.errorCode as ExportErrorCode]) {
    return EXPORT_ERROR_LABELS[job.errorCode as ExportErrorCode];
  }
  return job.errorMessage ?? undefined;
}

export interface ExportJob {
  sharingScope?: ReportSharingScope | string;
  isOwn?: boolean;
  exportId: string;
  reportCode: string;
  reportName: string;
  reportPeriod?: string;
  status: ExportJobStatus | 'Pending' | 'Running' | 'Ready' | 'Failed' | 'Cancelled' | 'Expired';
  source: ExportSource | string;
  scheduleId?: string;
  createdAt: string;
  completedAt?: string;
  downloadExpiresAt?: string;
  from?: string;
  toExclusive?: string;
  rowCount?: number;
  fileSize?: number;
  errorMessage?: string;
  errorCode?: ExportErrorCode | number | null;
  deliveryStatus: ExportDeliveryStatus | string;
  canCancel: boolean;
  canRetry: boolean;
  canDownload: boolean;
  canMarkFailed: boolean;
  canRetryDelivery?: boolean;
}
export interface ExportPage {
  items: ExportJob[];
  totalCount: number;
  page: number;
  pageSize: number;
}
export interface ExportQuery {
  ownOnly?: boolean;
  sharedOnly?: boolean;
  availableOnly?: boolean;
  from?: string;
  toExclusive?: string;
  reportCode?: string;
  scheduleId?: string;
  page?: number;
  pageSize?: number;
}
export interface SubmitExport {
  clientRequestId: string;
  reportCode: string;
  outputKind: ReportOutputKind | 'summary' | 'detail';
  parameters: ReportRunRequest;
  columns: string[];
  sort: ReportDefinition['sort'];
  voidStatus: string;
  chartCategory?: { groupBy: string; category: string };
}
type ExportRefreshOptions = Pick<SWRConfiguration<ExportPage>, 'refreshInterval' | 'revalidateOnFocus' | 'revalidateOnReconnect'>;

export function useExports(query: ExportQuery, options: ExportRefreshOptions = {}) {
  const { user } = useAuth();
  const { tenantId, selectedCompanySiteResult: scope } = useTenantSelection();
  const key = tenantId && user?.id ? ['exports', user.id, tenantId, scope?.companyId, scope?.siteId, query] : null;
  const result = useSWR<ExportPage>(key, async () => (await axios.get<ExportPage>('api/exports', { params: query })).data, {
    refreshInterval: 30_000,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    ...options
  });
  return { ...result, jobs: result.data?.items ?? [] };
}
export async function submitExport(value: SubmitExport): Promise<ExportJob> {
  const payload = {
    ...value,
    outputKind: typeof value.outputKind === 'string' ? (value.outputKind === 'summary' ? 1 : 2) : value.outputKind
  };
  const response = await axios.post<ExportJob>('api/exports', payload);
  await refreshExports();
  return response.data;
}
export function useExportOverview(reportCode: string) {
  const { user } = useAuth();
  const { tenantId } = useTenantSelection();
  return useSWR<{ latestAvailable?: ExportJob; outstandingDownloads: number }>(
    tenantId && user?.id ? ['exports', user.id, tenantId, 'overview', reportCode] : null,
    async () => (await axios.get('api/exports/overview', { params: { reportCode } })).data,
    { refreshInterval: 30_000, revalidateOnFocus: true, revalidateOnReconnect: true }
  );
}
export async function getExport(id: string): Promise<ExportJob> {
  return (await axios.get<ExportJob>(`api/exports/${encodeURIComponent(id)}`)).data;
}
export async function exportAction(id: string, action: 'cancel' | 'retry' | 'mark-failed' | 'retry-delivery'): Promise<void> {
  await axios.post(`api/exports/${encodeURIComponent(id)}/${action}`);
  await refreshExports();
}
export async function downloadExport(id: string, tenantId?: number): Promise<void> {
  const { data } = await axios.post<{ url: string }>(
    `api/exports/${encodeURIComponent(id)}/download-url`,
    undefined,
    tenantId ? { headers: { 'X-Tenant-Id': String(tenantId) } } : undefined
  );
  window.location.assign(data.url);
  await refreshExports();
}
export async function refreshExports() {
  await mutate((key: unknown) => Array.isArray(key) && key[0] === 'exports');
}
